import { useMemo, type CSSProperties } from "react";

interface GlassmorphismProps {
  colors: string[];
  opacity?: number;
  blur?: number;
  className?: string;
  overlayOpacity?: number;
}

export const Glassmorphism = ({
  colors,
  opacity = 0.5,
  blur = 10,
  className = "",
  overlayOpacity = 0,
}: GlassmorphismProps) => {
  const gradientStyle = useMemo((): CSSProperties => {
    const colorStops = colors
      .map((color, index) => {
        const percentage = (index * 100) / (colors.length - 1);
        return `${color} ${percentage}%`;
      })
      .join(", ");

    return {
      position: "absolute",
      inset: 0,
      background:
        colors.length > 1 ? `linear-gradient(45deg, ${colorStops})` : colors[0],
      opacity,
      backdropFilter: `blur(${blur}px)`,
      WebkitBackdropFilter: `blur(${blur}px)`, // For Safari support
      zIndex: 1,
      transition: "background 1.5s ease-in-out, opacity 1.5s ease-in-out",
    };
  }, [colors, opacity, blur]);

  const backgroundStyle = useMemo((): CSSProperties => {
    const solidColors = colors.map(
      (color) => color.replace(/[^,]+(?=\))/, "1") // Replace opacity value with 1
    );

    return {
      position: "absolute",
      inset: 0,
      background: `linear-gradient(to br, ${solidColors.join(", ")})`,
      zIndex: 0,
      transition: "background 1.5s ease-in-out",
    };
  }, [colors]);

  return (
    <div className={`relative ${className}`}>
      {/* Background gradient */}
      <div style={backgroundStyle} />

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute -inset-[40%] h-[200%] w-[200%] rotate-12 bg-gradient-to-l from-white/10 via-white/5 to-transparent" />
        <div className="absolute -inset-[40%] h-[200%] w-[200%] -rotate-12 bg-gradient-to-r from-white/10 via-white/5 to-transparent" />
      </div>

      {/* Glass effect overlay */}
      <div style={gradientStyle} />

      {/* Darkening overlay */}
      {overlayOpacity > 0 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "black",
            opacity: overlayOpacity,
            zIndex: 2,
            transition: "opacity 1.5s ease-in-out",
          }}
        />
      )}
    </div>
  );
};

export default Glassmorphism;
